/**
 * Role catalogue — role titles in use over time (M17, slide 18), and the catalogue
 * of roles as it stands (M20, slide 23).
 *
 * TITLES AND ROLES, NOT PEOPLE. A title counts once however many positions carry it
 * and however many people fill them, so a rising line here is the catalogue
 * widening, not the workforce growing. A count on a workforce page reads as
 * headcount unless it is labelled otherwise, so it is said on the chart, on the
 * composition and in the footnote.
 *
 * TWO READINGS, EACH NAMING ITS DATE:
 *   • extras.titles_trend is the series of distinct titles on approved positions at
 *     each date; extras.titles_in_use rides beside it with the function cut (on the
 *     catalogue it names), the growth and the untitled positions.
 *   • extras.role_catalogue counts ACTIVE ROLES. Its top-level keys are the grading
 *     of `catalogue_as_at` — the board pack's 31 July catalogue leads — with the
 *     current grading beside it in `current_catalogue`.
 * A role can sit in the catalogue with no approved position on it, so the two
 * readings differ, and the panel says why where they meet.
 *
 * Nothing here is withheld: counts of titles and roles identify nobody, and identity
 * publishes both blocks in full. A key the response leaves out is not available for
 * this date and says so; it is never drawn as zero. No figure is computed here —
 * the growth, the untitled positions and every catalogue count are identity's.
 */
import React, { useMemo, type ReactNode } from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
} from 'recharts';
import { BarRow } from './BarRow';
import { MetricTile, TileRow } from './MetricTile';
import { PanelShell, PanelSkeleton, PanelUnavailable } from './PanelShell';
import {
  ACCENT,
  AXIS,
  GRID,
  INK,
  Def,
  fmtDay,
  fmtMonth,
  fmtNum,
  fmtPct,
  panelDateLabel,
  readExtras,
  type DefLookup,
  type WorkforceBasis,
  type WorkforcePanels,
} from './apiContract';

interface Props {
  panels: WorkforcePanels | null;
  basis: WorkforceBasis;
  loading: boolean;
  def: DefLookup;
}

const NOT_AVAILABLE = 'not available for this date';

/** Muted tone: roles not graded sit outside the band structure, not in another band. */
const NON_GRADED_COLOR = '#B9C4D6';

/** Senior first; not graded, then no grade recorded, last. */
const BAND_ORDER = ['F', 'E', 'D', 'C', 'B', 'A'];

function bandRank(label: string): number {
  const l = label.trim().toLowerCase();
  if (l === '(no grade)') return 100;
  if (l.startsWith('non') || l.startsWith('ungraded') || l === 'ng') return 99;
  const i = BAND_ORDER.indexOf(label.trim().charAt(0).toUpperCase());
  return i === -1 ? 50 : i;
}

const isNonGradedBand = (label: string) => bandRank(label) >= 99;

/** Board words for the catalogue's placeholder keys. */
const KEY_LABELS: Record<string, string> = {
  '(unmapped)': 'No function recorded',
  '(no grade)': 'No grade recorded',
};
const keyLabel = (key: string) => KEY_LABELS[key] ?? key;

const largestFirst = (rec: Record<string, number> | undefined) =>
  Object.entries(rec ?? {}).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));

const maxOf = (entries: [string, number][]) => Math.max(0, ...entries.map(([, v]) => v));

// =============================================================================
// DEFINITIONS — server definitions win: the lookup is only consulted when it misses.
// =============================================================================

const defBody = (label: string, body: string, caveats: string[] = []): ReactNode => (
  <div className="space-y-1.5">
    <p className="font-semibold text-white">{label}</p>
    <p>{body}</p>
    {caveats.length ? (
      <ul className="list-disc space-y-0.5 pl-3.5 text-[11px] text-amber-200/90">
        {caveats.map((c, i) => (
          <li key={i}>{c}</li>
        ))}
      </ul>
    ) : null}
  </div>
);

const FALLBACK_DEFS: Record<string, ReactNode> = {
  titles_trend: defBody(
    'Role titles in use',
    'Distinct role titles carried by approved positions of every contract type at each date. A title '
      + 'counts once however many positions carry it.',
    [
      'A count of titles, not people or positions: a rising line is the catalogue widening, not '
        + 'headcount growth.',
      'Positions with no title recorded are not counted and are shown separately.',
    ],
  ),
  titles_in_use_by_function: defBody(
    'Titles in use by function',
    'Each title in use is counted once, under the function of the first position carrying it, read '
      + 'on the role catalogue of the date shown.',
  ),
  role_catalogue: defBody(
    'Role catalogue',
    'Active roles by function and grade band, as graded on the catalogue date shown, with the current '
      + 'grading beside it.',
    ['Roles, not people and not positions.'],
  ),
  role_catalogue_active: defBody(
    'Active roles',
    'Roles in the Role Master as graded on the date shown, counting those still active today. Whether '
      + 'a role is active is read today: the catalogue keeps no history of it.',
    ['Roles, not people and not positions: one role can carry many positions, or none.'],
  ),
  role_catalogue_non_graded: defBody(
    'Roles not graded',
    'Active roles carrying no grade band. They sit outside every band-based cut of the catalogue, so '
      + 'pay benchmarking, banded equity views and grade structure work all have to state them '
      + 'separately.',
    ['A count of roles, not of the people in them.'],
  ),
  role_catalogue_by_function: defBody(
    'Roles by function',
    'Active roles per Role Master function.',
    ['Role counts: a function with many roles has a wide catalogue, not necessarily a large headcount.'],
  ),
  role_catalogue_by_band: defBody(
    'Roles by grade band',
    'Active roles per grade band, senior band first, with roles not graded last.',
    ['Role counts — the shape of the catalogue, not the shape of the workforce.'],
  ),
};

/** Server definition first, local wording only where the server has none. */
function extendDefs(def: DefLookup, extra: Record<string, ReactNode>): DefLookup {
  return (key: string) => def(key) ?? extra[key];
}

/** A dash for a figure this date does not carry — never a zero. */
const NotAvailableValue: React.FC = () => (
  <span className="text-gray-400" title="Not available for this date">
    —
  </span>
);

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

export const RoleCataloguePanel: React.FC<Props> = ({ panels, basis, loading, def }) => {
  const d = useMemo(() => extendDefs(def, FALLBACK_DEFS), [def]);
  const trend = readExtras(panels, 'titles_trend');
  const inUse = readExtras(panels, 'titles_in_use');
  const cat = readExtras(panels, 'role_catalogue');

  const series = useMemo(
    () =>
      (Array.isArray(trend) ? trend : [])
        .filter((p) => p && typeof p.as_at === 'string' && p.as_at)
        .sort((a, b) => a.as_at.localeCompare(b.as_at)),
    [trend],
  );
  const last = useMemo(() => {
    const known = series.filter((p) => typeof p.titles === 'number');
    return known.length ? known[known.length - 1] : null;
  }, [series]);

  const titleFunctions = useMemo(() => largestFirst(inUse?.by_function), [inUse]);
  const byFunction = useMemo(() => largestFirst(cat?.by_function), [cat]);
  const byBand = useMemo(
    () =>
      Object.entries(cat?.by_band ?? {}).sort(
        (a, b) => bandRank(a[0]) - bandRank(b[0]) || a[0].localeCompare(b[0]),
      ),
    [cat],
  );

  /** The catalogue the lead reading is graded on; absent on a copy stored before it. */
  const graded = cat?.catalogue_as_at ?? null;
  const current = cat?.current_catalogue ?? null;
  const changes = cat?.catalogue_changes ?? null;
  const growth = inUse?.growth ?? null;
  const untitled = typeof inUse?.untitled_seats === 'number' ? inUse.untitled_seats : null;

  // Each block's own date and kind, never panels_as_at.
  const titlesDate = panelDateLabel(panels, 'titles_trend');
  const catalogueDate = panelDateLabel(panels, 'role_catalogue');

  const gradedSub = (fallback: string) => (graded ? `as graded on ${fmtDay(graded)}` : fallback);

  return (
    <PanelShell
      title="Role catalogue"
      defKey="titles_trend"
      def={d}
      window={
        <>
          {titlesDate === catalogueDate ? titlesDate : `Titles: ${titlesDate}. Roles: ${catalogueDate}`}.
          Chart: distinct titles on approved positions at each date shown. Composition: active roles
          {graded ? ` as graded on ${fmtDay(graded)}` : ' in the catalogue'}
          {current ? ", with today's grading beside it" : ''}. Neither follows the basis toggle, the
          period picker or the range slicer.
        </>
      }
      right={
        last ? (
          <div className="text-right">
            <div className="text-lg font-bold leading-none tabular-nums text-heritage-blue">
              {fmtNum(last.titles)}
            </div>
            <div className="text-[10px] text-gray-500">titles in use at {fmtDay(last.as_at)}</div>
          </div>
        ) : null
      }
      footnote={
        <>
          Titles and roles, not people and not positions: a title counts once however many positions
          carry it, and ICA and casual titles (marker, moderator, invigilator) are included — so the
          line rising is the catalogue widening, never headcount growth. Titles in the chart are
          counted on approved positions
          {basis === 'positions' ? '' : ', whichever basis is selected above'}. Counting titles and
          roles identifies nobody, so nothing here is withheld; a dash means the figure is not
          available for this date.
        </>
      }
    >
      {loading && !panels ? (
        <PanelSkeleton height={150} />
      ) : !panels ? (
        <PanelUnavailable what="The role catalogue" />
      ) : !series.length && !cat ? (
        <div className="rounded border border-gray-100 bg-gray-50 p-2.5 text-[11px] leading-snug text-gray-500">
          The role catalogue is not available for this date.
        </div>
      ) : (
        <>
          {!series.length ? (
            <div className="rounded border border-gray-100 bg-gray-50 p-2.5 text-[11px] leading-snug text-gray-500">
              Titles in use over time are {NOT_AVAILABLE} — only the catalogue as it stands, below.
            </div>
          ) : (
            <>
              <div style={{ width: '100%', height: 152 }}>
                <ResponsiveContainer>
                  <AreaChart data={series} margin={{ top: 6, right: 8, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                    <XAxis
                      dataKey="as_at"
                      tick={{ fontSize: 10 }}
                      tickLine={false}
                      axisLine={{ stroke: AXIS }}
                      tickFormatter={(v: string) => fmtMonth(v.slice(0, 7))}
                      interval={0}
                    />
                    <YAxis
                      tick={{ fontSize: 10 }}
                      tickLine={false}
                      axisLine={false}
                      width={36}
                      tickFormatter={(v: number) => fmtNum(v)}
                    />
                    <RechartsTooltip
                      labelStyle={{ fontWeight: 600 }}
                      contentStyle={{ fontSize: 12 }}
                      labelFormatter={(v: string) => fmtDay(v)}
                      formatter={(v: number) => [fmtNum(v), 'Job titles in use']}
                    />
                    <Area
                      type="monotone"
                      dataKey="titles"
                      name="Job titles in use"
                      stroke={INK}
                      strokeWidth={2}
                      fill={ACCENT}
                      fillOpacity={0.16}
                      dot={{ r: 2.5, fill: INK, strokeWidth: 0 }}
                      isAnimationActive={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              {last ? (
                <div className="mt-2 text-[12.5px] leading-snug tabular-nums text-gray-600">
                  <b className="text-heritage-blue">{fmtNum(last.titles)}</b> distinct job titles at{' '}
                  {fmtDay(last.as_at)}
                  {growth ? (
                    <>
                      , {growth.pct >= 0 ? 'up' : 'down'} {fmtPct(Math.abs(growth.pct))} from{' '}
                      {fmtDay(growth.from)} to {fmtDay(growth.to)}
                    </>
                  ) : null}
                  . <span className="text-gray-500">Titles, not people.</span>
                  {untitled !== null ? (
                    <>
                      {' '}
                      {fmtNum(untitled)}{' '}
                      {plural(untitled, 'position carries no title yet and is', 'positions carry no title yet and are')}{' '}
                      not counted.
                    </>
                  ) : null}
                </div>
              ) : null}

              {titleFunctions.length ? (
                <div className="mt-3">
                  <div className="flex items-center text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                    Titles in use by function
                    <Def def={d} k="titles_in_use_by_function" />
                  </div>
                  <div className="mb-1 text-[11px] leading-snug text-gray-500">
                    Each title counted once, under the function of the first position carrying it
                    {inUse?.catalogue_as_at
                      ? `, on the role catalogue of ${fmtDay(inUse.catalogue_as_at)}`
                      : ''}
                    .
                  </div>
                  {titleFunctions.map(([name, v]) => (
                    <BarRow
                      key={name}
                      label={keyLabel(name)}
                      value={v}
                      max={maxOf(titleFunctions)}
                      color={INK}
                      labelWidth={168}
                    />
                  ))}
                </div>
              ) : null}
            </>
          )}

          {/* ── the catalogue of roles, on the catalogue it names ── */}
          {cat ? (
            <div className="mt-4 space-y-3 border-t border-gray-100 pt-3">
              <div className="flex items-center text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                The catalogue as it stands — {catalogueDate}
                <Def def={d} k="role_catalogue" />
              </div>

              <TileRow min="170px">
                <MetricTile
                  label="Active roles"
                  value={fmtNum(cat.active)}
                  sub={gradedSub('roles in the catalogue — not people, not positions')}
                  defKey="role_catalogue_active"
                  def={d}
                  size="sm"
                />
                {graded ? (
                  <MetricTile
                    label="Active roles today"
                    value={current ? fmtNum(current.active) : <NotAvailableValue />}
                    sub={current ? `as graded on ${fmtDay(current.catalogue_as_at)}` : NOT_AVAILABLE}
                    defKey="role_catalogue_active"
                    def={d}
                    size="sm"
                  />
                ) : null}
                <MetricTile
                  label="Not graded"
                  value={fmtNum(cat.non_graded)}
                  sub={gradedSub('active roles carrying no grade band')}
                  defKey="role_catalogue_non_graded"
                  def={d}
                  size="sm"
                />
                {graded ? (
                  <MetricTile
                    label="Not graded today"
                    value={current ? fmtNum(current.non_graded) : <NotAvailableValue />}
                    sub={current ? `as graded on ${fmtDay(current.catalogue_as_at)}` : NOT_AVAILABLE}
                    defKey="role_catalogue_non_graded"
                    def={d}
                    size="sm"
                  />
                ) : null}
              </TileRow>

              <div
                className="grid gap-x-6 gap-y-3"
                style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}
              >
                {byFunction.length ? (
                  <div>
                    <div className="mb-1 flex items-center text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                      Roles by function{graded ? `, as graded on ${fmtDay(graded)}` : ''}
                      <Def def={d} k="role_catalogue_by_function" />
                    </div>
                    {byFunction.map(([name, v]) => (
                      <BarRow
                        key={name}
                        label={keyLabel(name)}
                        value={v}
                        max={maxOf(byFunction)}
                        color={INK}
                        labelWidth={148}
                      />
                    ))}
                  </div>
                ) : null}

                {byBand.length ? (
                  <div>
                    <div className="mb-1 flex items-center text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                      Roles by grade band{graded ? `, as graded on ${fmtDay(graded)}` : ''}
                      <Def def={d} k="role_catalogue_by_band" />
                    </div>
                    {byBand.map(([name, v]) => (
                      <BarRow
                        key={name}
                        label={keyLabel(name)}
                        value={v}
                        max={maxOf(byBand)}
                        // Not graded is drawn in the muted tone so it reads as
                        // "outside the band structure", not as another band.
                        color={isNonGradedBand(name) ? NON_GRADED_COLOR : ACCENT}
                        labelWidth={120}
                      />
                    ))}
                  </div>
                ) : null}
              </div>

              <div className="text-[11px] leading-snug text-gray-600">
                {changes && graded ? (
                  <>
                    Roles, not people and not positions. {fmtNum(cat.non_graded)} of the{' '}
                    {fmtNum(cat.active)} active roles carried no grade on {fmtDay(graded)}
                    {typeof cat.non_graded_with_filled_seat === 'number'
                      ? `, and ${fmtNum(cat.non_graded_with_filled_seat)} of those ${plural(
                          cat.non_graded_with_filled_seat,
                          'has',
                          'have',
                        )} someone in post`
                      : ''}
                    . Since then {fmtNum(changes.gained_a_grade)}{' '}
                    {plural(changes.gained_a_grade, 'has', 'have')} gained a grade,{' '}
                    {fmtNum(changes.recorded_as_not_graded)}{' '}
                    {plural(changes.recorded_as_not_graded, 'has', 'have')} been recorded as not graded
                    and {fmtNum(changes.new_role_codes)}{' '}
                    {plural(changes.new_role_codes, 'role has', 'roles have')} been added
                    {current
                      ? `: the catalogue today holds ${fmtNum(current.active)} active roles, ${fmtNum(
                          current.non_graded,
                        )} of them not graded`
                      : ''}
                    .
                  </>
                ) : (
                  <>
                    These are counts of roles in the catalogue, not people and not positions: one role
                    can be carried by many positions, or by none at all.
                  </>
                )}{' '}
                The chart counts the titles an approved position carries; the catalogue counts roles,
                which can exist with no position on them, so the two differ.
              </div>
            </div>
          ) : (
            <div className="mt-4 rounded border border-gray-100 bg-gray-50 p-2.5 text-[11px] leading-snug text-gray-500">
              The catalogue of roles is {NOT_AVAILABLE}.
            </div>
          )}
        </>
      )}
    </PanelShell>
  );
};
